import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../../context/Auth";
import { useParams } from "react-router-dom";
import Loading from "../../components/Loading/Loading";
import Swal from "sweetalert2";
import RoleBasedComponent from "../../components/RoleBasedComponent";
import {
  UserCog,
  User,
  Building2,
  Shield,
  Mail,
  CheckCircle,
  XCircle,
  Save,
  ArrowLeft,
  Edit3,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const UpdateUser = () => {
  const navigate = useNavigate();
  const { user_id } = useParams();
  const { requestWithAuth } = useAuth();
  const [newUserData, setNewUserData] = useState({});
  const [creatingUser, setCreatingUser] = useState(false);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setNewUserData({ ...newUserData, [e.target.name]: e.target.value });
  };

  const fetchBranches = async () => {
    try {
      const data = await requestWithAuth(
        `/company/branches/?pageSize=1000`,
        "GET"
      );
      setBranches(data?.results);
    } catch (error) { }
  };

  const fetchUser = async () => {
    try {
      setLoading(true);
      const data = await requestWithAuth(`/users/accounts/${user_id}/`, "GET");
      setNewUserData(data);
    } catch (error) {
      toast.error("Failed to get user details. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (e) => {
    e.preventDefault();
    if (newUserData.role === "cashier" && !newUserData.phone_number?.trim()) {
      toast.error("Phone number is required for cashier role");
      return;
    }
    setCreatingUser(true);
    const body = JSON.stringify(newUserData);
    try {
      const response = await requestWithAuth(
        `/users/accounts/${user_id}/`,
        "PUT",
        body,
        {
          "Content-Type": "application/json",
        }
      );
      setCreatingUser(false);
      Swal.fire({
        title: "Success",
        icon: "success",
        text: "User Updated Successfully",
      }).then(() => {
        navigate("/administration/users");
      });
    } catch (error) {
      toast.error("Failed to update user. Please try again later.");

      setCreatingUser(false);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchUser();
  }, []);

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => (window.location.href = "/administration/users")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="text-sm font-medium">Back to Users</span>
          </button>

          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Administration</span>
                <span>/</span>
                <span>Users</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">Edit</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Edit3 size={32} className="text-blue-600" />
                Update User
              </h1>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            {/* Card Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <UserCog className="text-blue-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    User Information
                  </h2>
                  <p className="text-sm text-gray-600 mt-0.5">
                    Update user details and access permissions
                  </p>
                </div>
              </div>
            </div>

            {/* Card Body */}
            <div className="p-6">
              <div className="space-y-6">
                {/* Personal Information Section */}
                <div>
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
                    <User size={18} className="text-gray-500" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                      Personal Information
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* First Name */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <User size={16} className="text-gray-500" />
                        First Name
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="first_name"
                        required
                        value={newUserData?.first_name || ""}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="Enter first name"
                      />
                    </div>

                    {/* Last Name */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <User size={16} className="text-gray-500" />
                        Last Name
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="last_name"
                        required
                        value={newUserData?.last_name || ""}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="Enter last name"
                      />
                    </div>

                    {/* Phone Number */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <User size={16} className="text-gray-500" />
                        Phone Number
                        {newUserData?.role === "cashier" && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        type="tel"
                        name="phone_number"
                        required={newUserData?.role === "cashier"}
                        value={newUserData?.phone_number || ""}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="Enter phone number"
                      />
                    </div>

                    {/* Username */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <Mail size={16} className="text-gray-500" />
                        Username
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="username"
                        disabled
                        required
                        value={newUserData?.username || ""}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm bg-gray-100 cursor-not-allowed"
                        placeholder="Username"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Username cannot be changed
                      </p>
                    </div>

                    {/* Account Status */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        {newUserData?.is_active ? (
                          <CheckCircle size={16} className="text-green-500" />
                        ) : (
                          <XCircle size={16} className="text-red-500" />
                        )}
                        Account Status
                      </label>
                      <select
                        name="is_active"
                        onChange={handleChange}
                        value={newUserData?.is_active}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all"
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Organization & Permissions Section */}
                <div>
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
                    <Shield size={18} className="text-gray-500" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                      Organization & Permissions
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Branch */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <Building2 size={16} className="text-gray-500" />
                        Branch
                        <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="branch"
                        required
                        onChange={handleChange}
                        value={newUserData?.branch || ""}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all"
                      >
                        <option value="">Select Branch</option>
                        {branches.map((branch, index) => (
                          <option key={index} value={branch.id}>
                            {branch.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Role */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <Shield size={16} className="text-gray-500" />
                        Role
                        <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="role"
                        required
                        onChange={handleChange}
                        value={newUserData?.role || ""}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all"
                      >
                        <option value="">Select Role</option>
                        <option value="cashier">Cashier</option>
                        <option value="dispenser">Dispenser</option>
                        <option value="branch_manager">Branch Manager</option>
                        <option value="it_auditor">Auditor</option>
                        <option value="warehouse_manager">
                          Warehouse Manager
                        </option>
                        <option value="stores">Stores</option>
                        <option value="admin">Administrator</option>
                        <RoleBasedComponent allowedRoles={["superadmin"]}>
                          <option value="superadmin">Super Admin</option>
                        </RoleBasedComponent>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card Footer */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      (window.location.href = "/administration/users")
                    }
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>

                  {creatingUser ? (
                    <button
                      type="button"
                      disabled
                      className="bg-blue-400 text-white rounded-lg px-6 py-3 flex items-center gap-2 cursor-not-allowed"
                    >
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Updating...
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={updateUser}
                      className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-6 py-3 font-medium transition-colors shadow-sm flex items-center gap-2"
                    >
                      <Save size={18} />
                      Update User
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Help Card */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users size={20} className="text-blue-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-blue-900 mb-2">
                  User Management Tips
                </h3>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>
                    • Update user information as needed. Required fields are
                    marked with an asterisk (*)
                  </li>
                  <li>
                    • Username cannot be changed once created for security
                    reasons
                  </li>
                  <li>
                    • Set account status to "Inactive" to temporarily disable
                    access
                  </li>
                  <li>
                    • Assign appropriate roles based on user responsibilities
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateUser;
